// The canonical evaluator for the shared rule language (spec §9.1).
//
// This is the ONE place the AND/OR rule semantics live on the TS side; the
// tutorial player, admin-web preview, share-web, and the resolution engine all
// call through here so there is no drift. A byte-for-byte equivalent lives in
// Swift for the iOS player (see little-loop-ios). The Postgres resolution RPCs
// re-implement the same semantics for the authoritative access decision —
// these client evaluators are for rendering/UX only and are never a security
// boundary.
import { isRuleAll, isRuleAny, isRuleNot } from '@little-loop/shared-types';
/**
 * Evaluate a rule against a flat map of resolved facts/variable values.
 * A null/undefined rule means "no constraint" → true.
 *
 * Semantics: empty `all` is true (vacuous AND); empty `any` is false (vacuous
 * OR). Missing facts read as the empty string, matching the legacy
 * single-condition behaviour.
 */
export function evaluateRule(rule, facts) {
    if (!rule)
        return true;
    if (isRuleAll(rule))
        return rule.all.every((child) => evaluateRule(child, facts));
    if (isRuleAny(rule))
        return rule.any.some((child) => evaluateRule(child, facts));
    if (isRuleNot(rule))
        return !evaluateRule(rule.not, facts);
    return evaluateLeaf(rule, facts);
}
function evaluateLeaf(leaf, facts) {
    const left = facts[leaf.variable] ?? '';
    const right = leaf.value;
    switch (leaf.op) {
        case 'eq':
            return left === asScalar(right);
        case 'neq':
            return left !== asScalar(right);
        case 'gt':
            return compareNumeric(left, asScalar(right)) > 0;
        case 'lt':
            return compareNumeric(left, asScalar(right)) < 0;
        case 'gte':
            return compareNumeric(left, asScalar(right)) >= 0;
        case 'lte':
            return compareNumeric(left, asScalar(right)) <= 0;
        case 'in':
            return asArray(right).includes(left);
        case 'not_in':
            return !asArray(right).includes(left);
        default:
            return true;
    }
}
function asScalar(v) {
    return Array.isArray(v) ? (v[0] ?? '') : v;
}
function asArray(v) {
    return Array.isArray(v) ? v : [v];
}
/**
 * Compare two strings as numbers when both parse cleanly, else fall back to
 * lexicographic compare so dates / version strings still order sensibly.
 * Returns -1 / 0 / 1.
 */
function compareNumeric(a, b) {
    const an = Number(a);
    const bn = Number(b);
    if (!Number.isNaN(an) && !Number.isNaN(bn)) {
        if (an < bn)
            return -1;
        if (an > bn)
            return 1;
        return 0;
    }
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
